import { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import { Link } from 'react-router-dom';
import TaskSkeleton from './TaskSkeleton';

export default function StreamUserList() {
    const { http, user } = AuthUser();
    const [streamUsers, setStreamUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [subscriptionFilter, setSubscriptionFilter] = useState('all');

    useEffect(() => {
        fetchStreamUsers();
    }, [statusFilter, subscriptionFilter]);

    const fetchStreamUsers = () => {
        setIsLoading(true);
        // Note: This would need a new API endpoint for listing stream users
        // For now, we'll simulate with a placeholder
        http.get('/stream_users').then((res) => {
            setIsLoading(false);
            if (res.data.success && Array.isArray(res.data.data)) {
                let filteredUsers = res.data.data;
                
                // Filter by status
                if (statusFilter !== 'all') {
                    filteredUsers = filteredUsers.filter(user => user.status === statusFilter);
                }
                
                // Filter by subscription
                if (subscriptionFilter !== 'all') {
                    filteredUsers = filteredUsers.filter(user => user.subscription === subscriptionFilter);
                }
                
                setStreamUsers(filteredUsers);
            } else {
                console.error("API response error:", res.data);
                toast.error("Failed to fetch stream users");
            }
        }).catch(error => {
            setIsLoading(false);
            console.error("Error fetching stream users:", error);
            toast.error("Error fetching stream users");
        });
    };

    const handleStatusChange = async (userId, newStatus) => {
        try {
            const response = await http.put(`/stream_users/${userId}/status`, {
                status: newStatus
            });
            
            if (response.data.success) {
                toast.success(`User status updated to ${newStatus}`);
                fetchStreamUsers();
            }
        } catch (error) {
            console.error("Error updating user status:", error);
            toast.error("Error updating user status");
        }
    };

    const handleSubscriptionChange = async (userId, newSubscription) => {
        try {
            const response = await http.put(`/stream_users/${userId}/subscription`, {
                subscription: newSubscription
            });
            
            if (response.data.success) {
                toast.success(`User subscription updated to ${newSubscription}`);
                fetchStreamUsers();
            }
        } catch (error) {
            console.error("Error updating user subscription:", error);
            toast.error("Error updating user subscription");
        }
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'active': 'badge bg-success',
            'inactive': 'badge bg-secondary',
            'suspended': 'badge bg-danger',
            'pending': 'badge bg-warning'
        };
        return statusClasses[status] || 'badge bg-secondary';
    };

    const getSubscriptionBadge = (subscription) => {
        const subscriptionClasses = {
            'free': 'badge bg-light text-dark',
            'premium': 'badge bg-primary',
            'pro': 'badge bg-info',
            'enterprise': 'badge bg-dark'
        };
        return subscriptionClasses[subscription] || 'badge bg-light text-dark';
    };

    return (
        <div className="container-fluid">
            <Toaster />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title">Stream Users</h3>
                            <div className="d-flex gap-2">
                                <select 
                                    className="form-select form-select-sm"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="suspended">Suspended</option>
                                    <option value="pending">Pending</option>
                                </select>
                                <select 
                                    className="form-select form-select-sm"
                                    value={subscriptionFilter}
                                    onChange={(e) => setSubscriptionFilter(e.target.value)}
                                >
                                    <option value="all">All Subscriptions</option>
                                    <option value="free">Free</option>
                                    <option value="premium">Premium</option>
                                    <option value="pro">Pro</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </div>
                        </div>
                        <div className="card-body">
                            {isLoading ? (
                                <TaskSkeleton />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Profile Image</th>
                                                <th>Status</th>
                                                <th>Subscription</th>
                                                <th>Country</th>
                                                <th>Last Login</th>
                                                <th>Created At</th>
                                                {/* <th>Actions</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {streamUsers.map((streamUser) => (
                                                <tr key={streamUser.id}>
                                                    <td>{streamUser.id}</td>
                                                    <td>
                                                        <div>
                                                            <strong>{streamUser.first_name} {streamUser.last_name}</strong>
                                                            {streamUser.bio && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-muted">{streamUser.bio}</small>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{streamUser.email}</td>
                                                    <td>{streamUser.phone || '-'}</td>
                                                    <td>
                                                        {streamUser.profile_image ? (
                                                            <img 
                                                                src={`http://3.6.125.26/api/api/stream_users/${streamUser.id}/image`}
                                                                alt="Profile"
                                                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                className="rounded-circle"
                                                            />
                                                        ) : (
                                                            <div 
                                                                className="rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white"
                                                                style={{ width: '40px', height: '40px' }}
                                                            >
                                                                {streamUser.first_name.charAt(0)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <select 
                                                            className={`form-select form-select-sm ${getStatusBadge(streamUser.status)}`}
                                                            value={streamUser.status}
                                                            onChange={(e) => handleStatusChange(streamUser.id, e.target.value)}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="suspended">Suspended</option>
                                                            <option value="pending">Pending</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <select 
                                                            className={`form-select form-select-sm ${getSubscriptionBadge(streamUser.subscription)}`}
                                                            value={streamUser.subscription}
                                                            onChange={(e) => handleSubscriptionChange(streamUser.id, e.target.value)}
                                                        >
                                                            <option value="free">Free</option>
                                                            <option value="premium">Premium</option>
                                                            <option value="pro">Pro</option>
                                                            <option value="enterprise">Enterprise</option>
                                                        </select>
                                                    </td>
                                                    <td>{streamUser.country || '-'}</td>
                                                    <td>
                                                        {streamUser.last_login_at ? 
                                                            moment(streamUser.last_login_at).format('DD/MM/YYYY HH:mm') : 
                                                            'Never'
                                                        }
                                                    </td>
                                                    <td>{moment(streamUser.created_at).format('DD/MM/YYYY HH:mm')}</td>
                                                    {/* <td>
                                                        <div className="btn-group" role="group">
                                                            <button 
                                                                className="btn btn-sm btn-info me-1"
                                                                title="View Details"
                                                            >
                                                                View
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-warning"
                                                                title="Edit User"
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
